import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Calendar,
  ChevronRight,
  Copy,
  Eraser,
  Pause,
  Pencil,
  Play,
  Settings,
  Trash2,
  UserPlus,
  RotateCcw,
} from "lucide-react";

import { Card, CardHeader, CardBody } from "../../components/Card/Card";
import { Avatar } from "../../components/Avatar/Avatar";
import { Badge } from "../../components/Badge/Badge";
import { Button } from "../../components/Button/Button";
import { Alert } from "../../components/Alert/Alert";
import { AlertStack } from "../../components/Alert/AlertStack";
import { EmptyState } from "../../components/EmptyState/EmptyState";
import { Menu } from "../../components/Menu/Menu";
import { PageHeader } from "../../components/PageHeader/PageHeader";
import { useToast } from "../../components/Toast/toastContext";
import { useAuth } from "../auth/useAuth";
import { getTeam, TeamMember } from "../team/api";
import { InviteMemberModal } from "../team/InviteMemberModal";
import {
  getProjectWipe,
  unsuspendProject,
  ProjectDetail,
  ProjectWipeTask,
} from "./api";
import { projectQueryKey, useProject } from "./useProject";
import { getApiKey } from "../apikeys/api";
import { AnalyticsCards } from "./AnalyticsCards";
import { OnlineBand } from "./OnlineBand";
import { RenameProjectModal } from "./RenameProjectModal";
import { DeleteProjectModal } from "./DeleteProjectModal";
import { SuspendProjectModal } from "./SuspendProjectModal";
import { WipeProjectDialog } from "./WipeProjectDialog";
import { ActivityLogCard } from "./ActivityLog/ActivityLogCard";
import {
  ClientVersionsCard,
  CompatibilityShimsCard,
} from "./ClientContractCards";
import {
  formatDuration,
  num,
  plural,
  projectWipeQueryKey,
  taskElapsedMs,
  useNow,
  wipeContactLost,
  WIPE_CONTACT_LOST_MS,
  type WipeCounts,
} from "./wipe";
import { ApiError, describeError } from "../../lib/errors";
import styles from "./ProjectOverviewPage.module.css";

// Date formatter — matches mockup "Oct 12, 2023" copy (L459).
const DATE_FMT = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
});

// Mockup hero ID code chip: `e74c…a82f` (L461). For non-UUIDish IDs we just
// show the first 8 chars to avoid a misleading ellipsis.
function shortId(id: string): string {
  if (!id) return "";
  if (id.length <= 9) return id;
  return `${id.slice(0, 4)}…${id.slice(-4)}`;
}

// Slightly longer Project-ID code chip in the Project details card — mockup
// uses `e74c9d10-…a82f` style (L562).
function midId(id: string): string {
  if (!id) return "";
  if (id.length <= 14) return id;
  return `${id.slice(0, 8)}-…${id.slice(-4)}`;
}

// Friendly "Last login" formatter. Falls back to absolute date for dates older
// than 30 days. Used in the Team members preview rows (mockup L491-L516).
function formatRelative(iso: string | null | undefined): string {
  if (!iso) return "—";
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return "—";
  const now = Date.now();
  const diff = Math.max(0, now - ts);
  const m = Math.floor(diff / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return DATE_FMT.format(new Date(ts));
}

/**
 * The completion toast, verbatim from docs/mockups/project-wipe.html.
 *
 * The two figures are the ones the dialog put in front of the operator when
 * they confirmed — nothing on the server counts players and guilds apart once
 * they are deleted, and the task reports rows per step. Without them the
 * sentence drops that clause instead of inventing a number: what it still says
 * — the project is empty and the API is open — is the half an operator acts
 * on.
 */
function wipedMessage(counts: WipeCounts | null): string {
  if (!counts) return "Project wiped. The game API is open again.";
  return `Project wiped — ${num(counts.players)} ${plural(
    counts.players,
    "player",
  )} and ${num(counts.guilds)} ${plural(
    counts.guilds,
    "guild",
  )} gone. The game API is open again.`;
}

/**
 * What a stopped wipe left behind — the mockup's own words for its most
 * important state (project-wipe.html L635-636), lifted off the dialog and onto
 * the page.
 *
 * They have to live here because the dialog is the one place an operator may
 * never look: the task runs on the server, closing the window does not stop
 * it, and the mockup's own footer invites closing it. A wipe that dies while
 * nobody is watching used to end with the banner vanishing, the badge back to
 * "Active" and the menu live again — a project reporting perfect health with
 * half its players gone. So the same sentences are pushed as a toast the
 * moment it stops, and stay on the page as a banner for as long as the latest
 * wipe is the one that failed.
 *
 * One clause differs from the mockup: "what you see above is already gone"
 * points at the dialog's step list, and there is nothing above a toast.
 */
const WIPE_STOPPED_TITLE = "The project is half empty";
const WIPE_STOPPED_BODY =
  "Nothing was rolled back — what the wipe removed is already gone. " +
  "The game API is open again. Running the wipe once more picks up where it " +
  "stopped.";

/** "Started 1m 12s ago by you." — the banner's second sentence. */
function wipeStartedLine(
  task: ProjectWipeTask,
  now: number,
  selfId: string | undefined,
): string {
  const ago = formatDuration(taskElapsedMs(task, now));
  // Null when the admin who started it has since been deleted. The clause is
  // dropped rather than filled with "someone": the row deliberately outlives
  // the account, and naming a ghost is worse than saying nothing.
  const by = task.started_by
    ? task.started_by.id === selfId
      ? "you"
      : task.started_by.email
    : null;
  return by ? `Started ${ago} ago by ${by}.` : `Started ${ago} ago.`;
}

export function ProjectOverviewPage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { push } = useToast();
  const { user } = useAuth();
  const qc = useQueryClient();
  const isSuperAdmin = user?.role === "super_admin";

  const [renameOpen, setRenameOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [suspendOpen, setSuspendOpen] = useState(false);
  /**
   * Whether the wipe dialog is up, and which of its two halves was asked for:
   * the menu offers a wipe ("question"), a banner shows the one that is there
   * ("task"). Null is closed.
   */
  const [wipeView, setWipeView] = useState<"question" | "task" | null>(null);
  /**
   * What the wipe dialog counted when the operator confirmed. Kept here rather
   * than in the dialog because the dialog is unmounted between the moment the
   * wipe starts and the moment it ends, and the toast that ends it belongs to
   * the page. Null when this browser did not ask the question — a page opened
   * onto a wipe already in flight.
   */
  const [wipeCounts, setWipeCounts] = useState<WipeCounts | null>(null);

  // Primary project fetch — the same entry every other screen of this project
  // reads. The rename and delete flows take their ETag off the record itself
  // (`data.etag`), which is the very value the header carries.
  const projectQ = useProject(id);

  // Top-4 team members for the preview card. The backend's team endpoint
  // returns all members; we slice on the client (it's small) and surface
  // failures silently — an EmptyState is rendered in that case.
  const teamQ = useQuery({
    queryKey: ["team", id],
    queryFn: () => getTeam(id),
    enabled: !!id && !!projectQ.data,
  });

  /**
   * The project's latest wipe task — the one thing on this page that changes
   * without anybody touching it.
   *
   * Every member asks, not only the super_admin who could start one. Starting
   * a wipe is a super_admin right; being told the project is mid-wipe is not,
   * and a member's page that skipped this question would go straight on to
   * fetch the analytics — pushing a count of a half-deleted project into the
   * server's ten-minute cache, which is the exact failure the pause below
   * exists to prevent, and it would poison the figures for everyone. What
   * stays behind the right is acting on the answer: the menu item and the
   * dialog.
   */
  const wipeQ = useQuery({
    queryKey: projectWipeQueryKey(id),
    queryFn: () => getProjectWipe(id),
    enabled: !!id,
    // Two seconds while it runs, and nothing at all otherwise: a finished task
    // never changes again, and polling a project that is not being wiped is a
    // request per two seconds for an answer nobody is waiting for.
    //
    // The third case is the endpoint that has stopped answering. `data` is the
    // last successful one and does not change when a fetch fails, so this
    // predicate keeps saying "running" and the poll keeps its two seconds —
    // times the global `retry: 1`, that is two requests every two seconds
    // against an endpoint already returning 500, for as long as the tab is
    // open.
    //
    // Backed off to the same window that decided contact was lost, and one
    // step rather than a 2 → 5 → 15 ladder: the ladder needs a count of
    // consecutive failures, which is the thing react-query does not keep (see
    // `wipeContactLost`), and it buys a few requests over a case that is
    // already an outage. Asking at the judgement's own period means the claim
    // the banner makes is re-tested exactly as often as it was made.
    refetchInterval: (query) => {
      if (query.state.data?.status !== "running") return false;
      return wipeContactLost(query.state) ? WIPE_CONTACT_LOST_MS : 2000;
    },
  });
  const wipeTask = wipeQ.data ?? null;
  const wiping = wipeTask?.status === "running";
  /**
   * The wipe is still believed to be running, and nothing has confirmed it for
   * fifteen seconds.
   *
   * `wiping` itself is deliberately NOT switched off here, and that is the
   * whole decision: it is what refuses Suspend, Wipe and Delete and what keeps
   * the analytics from counting, and flipping it because the server went quiet
   * would offer Delete over a project that may be halfway emptied and would
   * put a count of it into a ten-minute server-side cache — on a guess, and
   * for everyone. What must not survive losing contact is the CLAIM: a
   * percentage, a counter ticking up by the second and the words "until it
   * finishes" are all assertions about right now, made from an answer that may
   * be ten minutes old and describe a wipe that has since finished. So the
   * refusals stay and the sentence changes.
   */
  const wipeLostContact = wiping && wipeContactLost(wipeQ);

  /**
   * What the analytics row is allowed to do.
   *
   * "unknown" until the wipe query has answered — one extra round trip on page
   * open, and it buys the case this screen was drawn for: an operator
   * returning to a project mid-wipe. Without it the analytics request races
   * the wipe query, wins, and takes a count of a half-deleted project into a
   * ten-minute server-side cache. A wipe query that FAILED counts as an
   * answer: the row falls back to counting rather than shimmering forever over
   * an unrelated failure.
   */
  const wipeAnswered = wipeQ.isSuccess || wipeQ.isError;
  const analyticsPause = wiping ? "wiping" : wipeAnswered ? undefined : "unknown";

  /**
   * Which task this page has watched run, and which one it has already
   * announced.
   *
   * Refs rather than state: neither decides what is on screen, they only
   * remember what has been said, and a render for that would be a render for
   * nothing. The first is what stops the toast from firing on page load —
   * every visit to a project wiped last month arrives with that task as the
   * latest one, and congratulating the operator on it every time is worse than
   * not mentioning it. A wipe that FAILED is announced twice over for the same
   * reason in reverse: the toast here is for whoever was in the room when it
   * happened, and the banner below is for everyone who was not.
   */
  const watchedWipeRef = useRef<string | null>(null);
  const announcedWipeRef = useRef<string | null>(null);

  useEffect(() => {
    if (!wipeTask) return;
    if (wipeTask.status === "running") {
      watchedWipeRef.current = wipeTask.id;
      return;
    }
    if (watchedWipeRef.current !== wipeTask.id) return;
    if (announcedWipeRef.current === wipeTask.id) return;
    announcedWipeRef.current = wipeTask.id;

    if (wipeTask.status === "done") {
      push({ variant: "success", message: wipedMessage(wipeCounts) });
    } else {
      // The dialog may well be closed — the footer told the operator it could
      // be — so the outcome that matters most is the one that would otherwise
      // arrive as silence: the banner disappearing, the badge back to
      // "Active", and nothing anywhere saying the project is half empty.
      push({
        variant: "danger",
        title: WIPE_STOPPED_TITLE,
        message: WIPE_STOPPED_BODY,
      });
    }
    // A wipe that stopped halfway took rows too, so nothing below is
    // conditional on the outcome: every count this page shows was true about a
    // project that no longer exists, the analytics row has been refusing to
    // fetch while the wipe held it, and the activity feed has just gained the
    // entry that says this happened.
    void qc.invalidateQueries({ queryKey: ["project", id, "analytics"] });
    // Presence is one of the things a wipe deletes, and the band has been
    // holding its pre-wipe count all the way through.
    void qc.invalidateQueries({ queryKey: ["project", id, "online"] });
    void qc.invalidateQueries({ queryKey: ["project", id, "activity"] });
    void qc.invalidateQueries({ queryKey: projectQueryKey(id), exact: true });
    void qc.invalidateQueries({ queryKey: ["projects"] });
  }, [wipeTask, wipeCounts, id, push, qc]);

  const data: ProjectDetail | undefined = projectQ.data;

  function onStatusError(err: unknown) {
    push({
      variant: "danger",
      message:
        err instanceof ApiError
          ? describeError(err.code)
          : describeError("internal"),
    });
  }

  // Suspending has no mutation here: the confirmation dialog owns the request,
  // because both screens that offer a suspend want exactly the same thing done
  // afterwards. Unsuspending keeps one — it only ever restores service, so it
  // asks nothing and fires from the menu item itself.
  const unsuspendMut = useMutation({
    mutationFn: () => unsuspendProject(id),
    onSuccess: async () => {
      push({ variant: "success", message: "Project reactivated." });
      await qc.invalidateQueries({ queryKey: projectQueryKey(id) });
      await qc.invalidateQueries({ queryKey: ["projects"] });
      await qc.invalidateQueries({ queryKey: ["project", id, "activity"] });
    },
    onError: onStatusError,
  });

  const breadcrumb = [
    { label: "All projects", to: "/projects" },
    { label: data?.name ?? "Project" },
  ];

  const isSuspended = data?.status === "suspended";

  const headerActions = (
    <>
      {/*
        Analytics window is fixed at 7 days in v1 — a static chip, no dropdown
        affordance. See docs/mockups/project-overview.html.
      */}
      <span className={styles.rangeChip}>
        <Calendar size={13} aria-hidden="true" />
        <span>Last 7 days</span>
      </span>
      {isSuperAdmin && (
        <Menu
          aria-label="More project actions"
          items={[
            // Every destructive action is refused while a wipe runs, and each
            // says so where it would have been pressed: one condition greys
            // out three items, and an operator who is only told "no" reloads
            // the page to find out why.
            //
            // `wipeAnswered` is the same guard the analytics row carries, for
            // the same race and with more at stake: until the wipe query has
            // answered, "no wipe is running" is not something this page knows,
            // and in that window these three items are live over a project
            // that may be halfway emptied. Pressing "Wipe project data" there
            // opens a dialog whose task is null — `watching` initialises once,
            // stays false, and the progress view never arrives however far the
            // poll gets — and sends a preflight to count `count(*)` over every
            // large table of the project while rows are being deleted from
            // them. No `hint` for that state: it lasts one round trip, and a
            // word that appears and vanishes is worse than a moment's silence.
            isSuspended
              ? {
                  label: "Unsuspend project",
                  icon: <Play size={15} aria-hidden="true" />,
                  disabled: !data || wiping || !wipeAnswered,
                  hint: wiping ? "wiping" : undefined,
                  onSelect: () => unsuspendMut.mutate(),
                }
              : {
                  label: "Suspend project",
                  variant: "danger",
                  icon: <Pause size={15} aria-hidden="true" />,
                  disabled: !data || wiping || !wipeAnswered,
                  hint: wiping ? "wiping" : undefined,
                  onSelect: () => setSuspendOpen(true),
                },
            { separator: true },
            // Between Suspend and Delete, in that order for a reason: what is
            // offered first is emptying the project, and only then removing
            // it. The glyph is lucide `Eraser` and never `Trash2` — that one
            // belongs to the row below, and the two actions must not be
            // confusable at a glance.
            {
              label: "Wipe project data",
              variant: "danger",
              icon: <Eraser size={15} aria-hidden="true" />,
              disabled: !data || wiping || !wipeAnswered,
              hint: wiping ? "in progress" : undefined,
              onSelect: () => setWipeView("question"),
            },
            {
              label: "Delete project",
              variant: "danger",
              icon: <Trash2 size={15} aria-hidden="true" />,
              disabled: !data || wiping || !wipeAnswered,
              hint: wiping ? "wiping" : undefined,
              onSelect: () => setDeleteOpen(true),
            },
          ]}
        />
      )}
    </>
  );

  const header = (
    <PageHeader
      breadcrumb={breadcrumb}
      title="Overview"
      actions={headerActions}
    />
  );

  if (projectQ.isLoading) {
    return (
      <>
        {header}
        <div className={styles.page}>Loading…</div>
      </>
    );
  }
  if (projectQ.error) {
    return (
      <>
        {header}
        <div className={styles.page}>
          <Alert variant="danger" title="Couldn't load project">
            {projectQ.error instanceof ApiError
              ? describeError(projectQ.error.code)
              : describeError("internal")}
          </Alert>
        </div>
      </>
    );
  }
  if (!data) return header;

  const suspendedAlert = (
    <Alert variant="warn" title="This project is suspended.">
      Players can&apos;t authenticate or use social features until it&apos;s
      reactivated.
    </Alert>
  );

  /**
   * The dialog is a super_admin's, so the banners only offer their way into it
   * when there is one to offer. A member is still told what is happening to
   * this project — that is what the banner is for — but the button that would
   * open a dialog whose other button they may not press is not drawn.
   */
  const openWipeTask = isSuperAdmin ? () => setWipeView("task") : null;

  /**
   * Which of the wipe's ends the page is currently reporting.
   *
   * A running one until it stops, and then — only if it stopped badly — the
   * report of that, for as long as it is the LATEST wipe. The project really
   * is half empty until somebody runs another one, so the banner is not
   * dismissible and does not expire; a wipe that later succeeds replaces the
   * task this reads and takes the banner with it.
   */
  const wipeAlert =
    wiping && wipeTask ? (
      wipeLostContact ? (
        <WipeLostContactBanner percent={wipeTask.percent} />
      ) : (
        <WipeBanner task={wipeTask} selfId={user?.id} onView={openWipeTask} />
      )
    ) : wipeTask?.status === "failed" ? (
      <WipeStoppedBanner onView={openWipeTask} />
    ) : null;

  return (
    <>
      {header}
      <div className={styles.page}>
        {/* A wipe is announced above everything else on the page: the operator
            who closed the dialog and came back has to be told what is
            happening — or what happened — to this project before they read a
            single number on it. Alone it carries its own 16px; beside the
            suspended banner the gap between the two is AlertStack's business,
            not this page's. */}
        {wipeAlert &&
          (data.status === "suspended" ? (
            <AlertStack className={styles.wipeBanner}>
              {wipeAlert}
              {suspendedAlert}
            </AlertStack>
          ) : (
            <div className={styles.wipeBanner}>{wipeAlert}</div>
          ))}

        <HeroCard
          project={data}
          wiping={wiping}
          canRename={isSuperAdmin}
          onRename={() => setRenameOpen(true)}
        />

        {/* When a wipe banner is up this one has moved into the stack above —
            two warnings about the same project, one under the other, with the
            hero card between them is how an operator misses the second. */}
        {data.status === "suspended" && !wipeAlert && (
          <div className={styles.suspendedAlert}>{suspendedAlert}</div>
        )}

        {/* Presence, above the analytics row and with a caption of its own.
            It is a separate band and not a fourth card because it answers a
            different question from a different source: the row below counts a
            7-day window out of a ten-minute cache, this counts right now out
            of a one-second one — and «last 7 days · cached ~10 min» under the
            row stays true of exactly the three cards it belongs to. */}
        <OnlineBand
          projectId={id}
          paused={analyticsPause}
          wipeStartedAt={wipeTask?.started_at}
        />

        {/* Phase 2 — game-level analytics. See docs/mockups/project-overview.html.
            `wipeStartedAt` is what stops the row printing the counts it was
            holding when the wipe began — the pause below stops it asking, not
            holding. */}
        <AnalyticsCards
          projectId={id}
          paused={analyticsPause}
          wipeStartedAt={wipeTask?.started_at}
        />

        <div className={styles.grid}>
          {/* Left column: Team members + Activity log */}
          <div className={styles.column}>
            <TeamMembersCard
              projectId={id}
              members={teamQ.data?.members}
              error={teamQ.error}
              loading={teamQ.isLoading}
              onInviteClick={() => setInviteOpen(true)}
            />
            <ActivityLogCard projectId={id} />
          </div>

          {/* Right column: API key + Project details + Quick links */}
          <div className={styles.column}>
            <ProductionApiKeyCard projectId={id} />
            <ProjectDetailsCard
              project={data}
              onCopyId={async () => {
                try {
                  await navigator.clipboard.writeText(data.id);
                  push({ variant: "success", message: "Project ID copied." });
                } catch {
                  push({
                    variant: "danger",
                    message: "Couldn't copy — clipboard unavailable.",
                  });
                }
              }}
            />
            <QuickLinksCard
              projectId={id}
              onInviteClick={() => setInviteOpen(true)}
              onConfigClick={() => navigate(`/projects/${id}/config`)}
            />
          </div>
        </div>

        {/* The compatibility mechanism, in a grid row of its OWN and not
            appended to the two columns above. Read as one pair — «who is out
            there» beside «what we still carry for them» — so their top edges
            have to line up, and inside the columns they would sit however far
            apart the activity log above them happened to run on that project.
            The mockup draws them as a separate row for the same reason. */}
        <div className={`${styles.grid} ${styles.gridNext}`}>
          <ClientVersionsCard projectId={id} />
          <CompatibilityShimsCard projectId={id} />
        </div>

        {renameOpen && (
          <RenameProjectModal
            open={renameOpen}
            onClose={() => setRenameOpen(false)}
            project={data}
          />
        )}

        {suspendOpen && (
          <SuspendProjectModal
            open={suspendOpen}
            onClose={() => setSuspendOpen(false)}
            project={{ id: data.id, name: data.name }}
          />
        )}

        {deleteOpen && (
          <DeleteProjectModal
            open={deleteOpen}
            onClose={() => setDeleteOpen(false)}
            project={{
              id: data.id,
              name: data.name,
              etag: data.etag,
            }}
            onDeleted={() => {
              setDeleteOpen(false);
              push({ variant: "success", message: "Project deleted." });
              void qc.invalidateQueries({ queryKey: ["projects"] });
              navigate("/projects");
            }}
          />
        )}

        {wipeView && (
          <WipeProjectDialog
            projectId={id}
            projectName={data.name}
            task={wipeTask}
            view={wipeView}
            startedWith={wipeCounts}
            onStarted={setWipeCounts}
            onClose={() => setWipeView(null)}
          />
        )}

        <InviteMemberModal
          projectId={id}
          open={inviteOpen}
          onClose={() => setInviteOpen(false)}
        />
      </div>
    </>
  );
}

/* ===== Running-wipe banner (mockup L686-L695) ===== */

function WipeBanner({
  task,
  selfId,
  onView,
}: {
  task: ProjectWipeTask;
  selfId: string | undefined;
  /** Null for anyone who may not open the dialog this would open. */
  onView: (() => void) | null;
}) {
  /**
   * The ticking clock lives in the one component that prints it.
   *
   * Held on the page it re-rendered the hero, the three analytics cards, the
   * team list, the activity log and a freshly built `<Menu>` items array once
   * a second, all so that a single "1m 12s" could count. This component is
   * mounted only while the wipe runs, which is precisely when the clock has to
   * move, so `true` here is not a condition that got lost — it is the
   * condition.
   */
  const now = useNow(true);
  return (
    <Alert
      variant="info"
      // The banner's own spinner instead of the variant's `Info` glyph: this
      // is the one alert on the page that describes something in motion.
      icon={<span className={styles.bannerSpinner} />}
      // …and the one whose words move with it. `Alert` is a `role="alert"`,
      // which is assertive and atomic, so the second the counter below ticks
      // from "1m 12s" to "1m 13s" the whole region — title, body and the
      // button — is read out again over whatever the user was in the middle
      // of. A wipe runs for minutes; that is hundreds of interruptions.
      //
      // Silenced rather than the alternative, `aria-hidden` on the elapsed
      // line: that also stops the ticking, but by deleting "Started 1m 12s ago
      // by <who>" from the accessibility tree, and while the wipe runs this
      // banner is the only place on the page that says who started it — the
      // activity entry is not written until it ends. Nothing is lost by going
      // quiet, because this banner is not news: it is the state of the project
      // as it was found on arrival, said again by the "API closed" badge in
      // the hero and by the dimmed analytics row. The wipe's actual events —
      // it stopped, it finished — arrive as toasts, and the stopped banner
      // beside this one keeps its assertive announcement precisely because its
      // text never moves after it appears.
      aria-live="off"
      title={`Wiping project data — ${task.percent}%`}
      action={
        onView && (
          <Button variant="secondary" size="sm" onClick={onView}>
            View progress
          </Button>
        )
      }
    >
      The game API is closed for this project until it finishes.{" "}
      {wipeStartedLine(task, now, selfId)}
    </Alert>
  );
}

/* ===== The wipe query has stopped answering ===== */

/**
 * What the page says when it can no longer confirm the wipe it is reporting.
 *
 * The running banner one line up is a live report — a percentage, an elapsed
 * counter, "until it finishes" — and every word of it is a claim about this
 * second. After fifteen seconds of 500s or 403s those words describe a task
 * that may have finished ten minutes ago, and nothing on this page would ever
 * say so: `NetworkBanner` only speaks for `NetworkError`, and a server that
 * answers 500 was heard.
 *
 * It draws no "View progress": the dialog would open onto the same frozen task
 * and offer to watch a bar that has stopped moving. The one useful move is a
 * reload, and it is the only one the body asks for.
 *
 * Keeps the announcement `role="alert"` gives it — unlike the running banner,
 * which is silenced because its text ticks. This one appears once and never
 * changes a character afterwards; the percentage in the title is a memory, and
 * memories do not move.
 */
function WipeLostContactBanner({ percent }: { percent: number }) {
  return (
    <Alert
      variant="warn"
      title={`Lost contact with the wipe — last seen at ${percent}%`}
    >
      Social Hub API stopped answering, so there is no telling whether it is
      still running. Until it answers again this project is treated as
      mid-wipe: the counts stay paused and nothing destructive is offered.
      Reload the page to try again.
    </Alert>
  );
}

/* ===== Stopped-wipe banner (mockup L632-L638, moved onto the page) ===== */

/**
 * The report of a wipe that stopped, for everyone who was not watching.
 *
 * It has no counterpart in the mockup, which draws this state only inside the
 * dialog — but the same mockup insists the task lives on the server, that the
 * window may be closed, and that each of its three ends must read without
 * guesswork. The dialog can only satisfy that for the one browser that watched
 * it die. Nothing else on this page says a word: the badge goes back to
 * "Active", the menu comes alive, the analytics start counting again, and all
 * of it is true of a project missing half its players.
 */
function WipeStoppedBanner({ onView }: { onView: (() => void) | null }) {
  return (
    <Alert
      variant="danger"
      title={WIPE_STOPPED_TITLE}
      action={
        onView && (
          <Button variant="secondary" size="sm" onClick={onView}>
            See what stopped
          </Button>
        )
      }
    >
      {WIPE_STOPPED_BODY}
    </Alert>
  );
}

/* ===== Hero card (mockup L451-L465) ===== */

function HeroCard({
  project,
  wiping,
  canRename,
  onRename,
}: {
  project: ProjectDetail;
  /** A wipe is running, which is a truer thing to say than "Active". */
  wiping: boolean;
  canRename: boolean;
  onRename: () => void;
}) {
  const memberLabel = `${project.members_count} team ${
    project.members_count === 1 ? "member" : "members"
  }`;
  const created = DATE_FMT.format(new Date(project.created_at));
  return (
    <div className={styles.hero}>
      <div className={styles.heroIcon}>
        <Avatar name={project.name} size={56} />
      </div>
      <div className={styles.heroInfo}>
        <h2 className={styles.heroName}>
          {project.name}
          {/* Real status from the backend — Active or Suspended (L671). A
              running wipe outranks both: the stored status still says active,
              and the game API is answering 503 to every player holding a
              token. What the badge reports is what a client would find. */}
          {wiping ? (
            <Badge variant="warn">API closed</Badge>
          ) : project.status === "suspended" ? (
            <Badge variant="danger">Suspended</Badge>
          ) : (
            <Badge variant="success">Active</Badge>
          )}
        </h2>
        <div className={styles.heroMeta}>
          <span>
            <strong>{project.members_count}</strong>{" "}
            {project.members_count === 1 ? "team member" : "team members"}
          </span>
          <span className={styles.sep}>·</span>
          <span>Created {created}</span>
          <span className={styles.sep}>·</span>
          <span>
            ID <code>{shortId(project.id)}</code>
          </span>
          {/* Hidden helper for testing-library — gives a stable role-less hit */}
          <span aria-hidden hidden>
            {memberLabel}
          </span>
        </div>
      </div>
      {canRename && (
        <div className={styles.heroActions}>
          <Button variant="secondary" size="sm" onClick={onRename}>
            <Pencil size={14} aria-hidden="true" />
            <span>Rename</span>
          </Button>
        </div>
      )}
    </div>
  );
}

/* ===== Team members card (mockup L481-L520) ===== */

function TeamMembersCard({
  projectId,
  members,
  error,
  loading,
  onInviteClick,
}: {
  projectId: string;
  members: TeamMember[] | undefined;
  error: unknown;
  loading: boolean;
  onInviteClick: () => void;
}) {
  const top = useMemo(() => (members ?? []).slice(0, 4), [members]);
  const count = members?.length ?? 0;

  return (
    <Card>
      <CardHeader>
        <h3>Team members</h3>
        <span className={styles.cardHeaderRight}>
          {count > 0 && (
            <Link
              className={styles.viewAll}
              to={`/projects/${projectId}/team`}
            >
              View all ({count}) →
            </Link>
          )}
        </span>
      </CardHeader>
      <CardBody>
        {loading && <div className={styles.dim}>Loading members…</div>}
        {!loading && !!error && (
          <EmptyState
            title="Couldn't load team"
            description="Refresh the page to try again."
          />
        )}
        {!loading && !error && top.length === 0 && (
          <EmptyState
            title="No team members yet"
            description="Invite teammates to collaborate on this project."
            action={
              <Button onClick={onInviteClick}>
                <UserPlus size={14} aria-hidden="true" />
                <span>Invite member</span>
              </Button>
            }
          />
        )}
        {!loading && !error && top.length > 0 && (
          <div className={styles.memberList}>
            {top.map((m) => (
              <div key={m.user_id} className={styles.memberRow}>
                <Avatar name={m.name || m.email} size={32} />
                <span className={styles.memberInfo}>
                  <span className={styles.memberName}>{m.email}</span>
                  <span className={styles.memberMeta}>
                    Joined · {formatRelative(m.joined_at)}
                  </span>
                </span>
                {/*
                  Backend "status" field encodes membership state, not
                  role. The mockup distinguishes "Super admin" vs "Member";
                  without a per-membership role we fall back to "Member" and
                  defer the admin highlight to when the API exposes it.
                */}
                <Badge variant="user">Member</Badge>
              </div>
            ))}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

/* ===== Production API key card (mockup L541-L556) ===== */

function ProductionApiKeyCard({ projectId }: { projectId: string }) {
  const navigate = useNavigate();
  // Current API key read-state. Degrades gracefully — while loading or on a
  // 404/error the card still renders with em-dash placeholders.
  const apiKeyQ = useQuery({
    queryKey: ["project", projectId, "api-keys"],
    queryFn: () => getApiKey(projectId),
    enabled: !!projectId,
  });
  const apiKey = apiKeyQ.data;
  const last4 = apiKey?.last4 ?? "????";
  const rotatedDisplay = apiKey?.rotated_at
    ? DATE_FMT.format(new Date(apiKey.rotated_at))
    : apiKey
      ? "Never"
      : "—";
  const rotatedByDisplay = apiKey?.rotated_by?.email ?? "—";
  return (
    <Card>
      <CardHeader>
        <h3>Production API key</h3>
        <Badge variant="success" dot>
          Active
        </Badge>
      </CardHeader>
      <CardBody>
        <div className={styles.fpDisplay}>
          <span className={styles.fpDots}>••••••••••••••••••••••••</span>
          <span className={styles.fpTail}>{last4}</span>
        </div>
        <div className={styles.fpMeta}>
          <div>
            <div className="k">Rotated</div>
            <div className="v">{rotatedDisplay}</div>
          </div>
          <div>
            <div className="k">By</div>
            <div className="v">{rotatedByDisplay}</div>
          </div>
        </div>
        <Button
          variant="secondary"
          className={styles.rotateBtn}
          onClick={() => navigate(`/projects/${projectId}/api-keys`)}
        >
          <RotateCcw size={13} aria-hidden="true" />
          <span>Rotate or manage</span>
        </Button>
      </CardBody>
    </Card>
  );
}

/* ===== Project details card (mockup L558-L569) ===== */

function ProjectDetailsCard({
  project,
  onCopyId,
}: {
  project: ProjectDetail;
  onCopyId: () => void;
}) {
  const created = DATE_FMT.format(new Date(project.created_at));
  return (
    <Card>
      <CardHeader>
        <h3>Project details</h3>
      </CardHeader>
      <CardBody>
        <div className={styles.kvList}>
          <div className={styles.kvRow}>
            <span className={styles.kvKey}>Project ID</span>
            <span className={styles.kvValue}>
              <code title={project.id}>{midId(project.id)}</code>
              <button
                type="button"
                className={styles.copyBtn}
                aria-label="Copy project ID"
                onClick={onCopyId}
              >
                <Copy size={12} aria-hidden="true" />
              </button>
            </span>
          </div>
          <div className={styles.kvRow}>
            <span className={styles.kvKey}>Created</span>
            <span className={styles.kvValue}>{created}</span>
          </div>
          <div className={styles.kvRow}>
            <span className={styles.kvKey}>Created by</span>
            <span className={styles.kvValue}>
              {project.created_by?.email ? (
                project.created_by.email
              ) : (
                <span className={styles.dim}>—</span>
              )}
            </span>
          </div>
          <div className={styles.kvRow}>
            <span className={styles.kvKey}>Pending invites</span>
            <span className={styles.kvValue}>
              {project.pending_invites_count}
              <Link
                to={`/projects/${project.id}/team`}
                className={styles.inlineLink}
                aria-label="View team & invites"
              >
                View
              </Link>
            </span>
          </div>
          <div className={styles.kvRow}>
            <span className={styles.kvKey}>Status</span>
            <span className={styles.kvValue}>
              {project.status === "suspended" ? (
                <Badge variant="danger">Suspended</Badge>
              ) : (
                <Badge variant="success">Active</Badge>
              )}
            </span>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

/* ===== Quick links card (mockup L571-L597) ===== */

function QuickLinksCard({
  projectId,
  onInviteClick,
  onConfigClick,
}: {
  projectId: string;
  onInviteClick: () => void;
  onConfigClick: () => void;
}) {
  return (
    <Card>
      <CardHeader>
        <h3>Quick links</h3>
      </CardHeader>
      <CardBody>
        <Link
          to={`/projects/${projectId}/config`}
          className={styles.quickLink}
          onClick={(e) => {
            // Allow modifier-clicks to fall through to the Link's default nav.
            if (e.metaKey || e.ctrlKey || e.shiftKey) return;
            e.preventDefault();
            onConfigClick();
          }}
        >
          <span className={styles.quickTile} aria-hidden="true">
            <Settings size={16} />
          </span>
          <span className={styles.quickInfo}>
            <span className={styles.quickName}>Project config</span>
            <span className={styles.quickMeta}>
              Chat, guild, help requests…
            </span>
          </span>
          <ChevronRight
            size={14}
            aria-hidden="true"
            className={styles.quickChev}
          />
        </Link>
        <button
          type="button"
          className={styles.quickLink}
          onClick={onInviteClick}
        >
          <span className={styles.quickTile} aria-hidden="true">
            <UserPlus size={16} />
          </span>
          <span className={styles.quickInfo}>
            <span className={styles.quickName}>Invite member</span>
            <span className={styles.quickMeta}>
              Add someone to this project
            </span>
          </span>
          <ChevronRight
            size={14}
            aria-hidden="true"
            className={styles.quickChev}
          />
        </button>
      </CardBody>
    </Card>
  );
}
